import { useEffect, useState } from 'react'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import { CircleCheck, LifeBuoy, MessageSquare } from 'lucide-react'
import { AppShell, Crumb, EmptyState, Segmented, SkeletonCards, Surface, formatDateTime, tokens } from '@agribot/polygon-checks-ui'
import type { HelpRequest } from '@agribot/sourcing-model'
import { useAnswerHelpRequest, useCloseHelpRequest, useHelpRequests, useRoles, type HelpStatusFilter } from '../api'

const STATUS: Record<HelpRequest['status'], { label: string; bg: string; fg: string }> = {
  open: { label: 'Waiting for us', bg: tokens.color.tintWarning, fg: '#7d5a1a' },
  answered: { label: 'Answered', bg: tokens.color.brandTeal100, fg: '#18576e' },
  closed: { label: 'Closed', bg: tokens.color.ink100, fg: tokens.color.ink600 },
}

function StatusChip({ status }: { status: HelpRequest['status'] }) {
  const s = STATUS[status]
  return <Box component="span" sx={{ display: 'inline-flex', height: 22, px: 2, alignItems: 'center', borderRadius: 999, backgroundColor: s.bg, color: s.fg, font: `500 12px ${tokens.font.sans}`, whiteSpace: 'nowrap' }}>{s.label}</Box>
}

/**
 * Questions suppliers asked from the portal, and the conversation about
 * each: the RFI's "supplier requests". Anyone who reviews the supplier (or
 * an admin) sees them and answers; the supplier is emailed each answer.
 * Closed once the question is settled.
 */
export function HelpRequests() {
  const [status, setStatus] = useState<HelpStatusFilter>('active')
  const { data, isPending, error } = useHelpRequests(status)
  const [selected, setSelected] = useState<string | null>(null)
  useEffect(() => {
    if (data && !data.some((r) => r.id === selected)) setSelected(data[0]?.id ?? null)
  }, [data, selected])
  const current = data?.find((r) => r.id === selected) ?? null

  return (
    <AppShell crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Supplier requests</Crumb>]}>
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 6, mb: 6, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="h1" sx={{ mb: 2 }}>Supplier requests</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
              Questions suppliers asked from the portal. Answer here: the supplier is emailed your reply with a link back in.
              Close a request once it is settled.
            </Typography>
          </Box>
          <Segmented
            ariaLabel="Which requests"
            value={status}
            onChange={(v) => setStatus(v as HelpStatusFilter)}
            options={[
              { value: 'active', label: 'Not closed' },
              { value: 'open', label: 'Waiting for us' },
              { value: 'answered', label: 'Answered' },
              { value: 'closed', label: 'Closed' },
              { value: 'all', label: 'All' },
            ]}
          />
        </Box>
        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {isPending && <SkeletonCards columns="360px 1fr" />}
        {data && data.length === 0 && (
          <Surface><EmptyState title="No requests" body="Questions suppliers send from the portal appear here." /></Surface>
        )}
        {data && data.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px minmax(0, 1fr)' }, gap: 5, alignItems: 'start' }}>
            <Surface component="ul" aria-label="Requests" sx={{ listStyle: 'none', m: 0, p: 0, overflow: 'hidden' }}>
              {data.map((r, i) => (
                <Box component="li" key={r.id} sx={{ borderTop: i ? `1px solid ${tokens.color.ink200}` : 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelected(r.id)}
                    aria-current={r.id === selected ? 'true' : undefined}
                    sx={{
                      display: 'block', width: '100%', textAlign: 'left', border: 0, cursor: 'pointer', px: 4, py: 3.5,
                      backgroundColor: r.id === selected ? tokens.color.ink50 : 'transparent', font: 'inherit', color: 'inherit',
                      '&:hover': { backgroundColor: tokens.color.ink50 }, '&:focus-visible': { boxShadow: `inset ${tokens.shadow.ring}` },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Box sx={{ flex: 1, font: `600 14px ${tokens.font.sans}`, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subject}</Box>
                      <StatusChip status={r.status} />
                    </Box>
                    <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary' }}>
                      {r.supplier}{r.material ? ` · ${r.material}` : ''} · {formatDateTime(r.updatedAt)}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Surface>
            {current && <Thread request={current} />}
          </Box>
        )}
      </Box>
    </AppShell>
  )
}

function Thread({ request }: { request: HelpRequest }) {
  const answer = useAnswerHelpRequest()
  const close = useCloseHelpRequest()
  const canAct = useRoles().some((r) => r === 'admin' || r === 'buyer' || r === 'regional_lead')
  const [reply, setReply] = useState('')
  useEffect(() => { setReply(''); answer.reset() }, [request.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const closed = request.status === 'closed'

  return (
    <Surface sx={{ p: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 1 }}>
        <LifeBuoy size={20} color={tokens.color.brandDeep} style={{ flex: 'none', marginTop: 4 }} />
        <Typography variant="h3" component="h2" sx={{ flex: 1 }}>{request.subject}</Typography>
        <StatusChip status={request.status} />
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, ml: 8 }}>
        {request.supplier}{request.material ? ` · about ${request.material}` : ''} · asked {formatDateTime(request.createdAt)}
      </Typography>
      <Box component="ol" aria-label="Messages" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 3, mb: 5 }}>
        {request.messages.map((m) => (
          <Box
            component="li"
            key={m.id}
            sx={{
              maxWidth: '85%', alignSelf: m.fromSupplier ? 'flex-start' : 'flex-end', p: 4, borderRadius: `${tokens.radius.lg}px`,
              backgroundColor: m.fromSupplier ? tokens.color.ink50 : tokens.color.brandTeal100,
            }}
          >
            <Box sx={{ font: `500 12px ${tokens.font.sans}`, color: 'text.secondary', mb: 1 }}>
              {m.author ?? (m.fromSupplier ? request.supplier : 'Us')} · {formatDateTime(m.createdAt)}
            </Box>
            <Box sx={{ font: `400 14px ${tokens.font.sans}`, whiteSpace: 'pre-wrap' }}>{m.body}</Box>
          </Box>
        ))}
      </Box>
      {canAct && !closed && (
        <Box component="form" onSubmit={(e) => {
          e.preventDefault()
          answer.mutate({ id: request.id, message: reply.trim() }, { onSuccess: () => setReply('') })
        }}>
          <TextField
            label="Your answer"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 4000 }}
          />
          {answer.isError && <Alert severity="error" sx={{ mt: 2 }}>{(answer.error as Error).message}</Alert>}
          {close.isError && <Alert severity="error" sx={{ mt: 2 }}>{(close.error as Error).message}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="text" startIcon={<CircleCheck size={16} />} disabled={close.isPending} onClick={() => close.mutate(request.id)}>Close request</Button>
            <Button type="submit" startIcon={<MessageSquare size={16} />} disabled={!reply.trim() || answer.isPending}>Send answer</Button>
          </Box>
        </Box>
      )}
      {closed && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Closed. The supplier can ask a new question from the portal.</Typography>}
    </Surface>
  )
}
